import React, { useRef, useState } from "react";
import {
	Autocomplete,
	AutocompleteProps,
	Box,
	CircularProgress,
	createFilterOptions,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
	SxProps,
	TextField,
	TextFieldProps,
	Theme,
	Tooltip,
} from "@mui/material";
import { ConditionalNullable, InputError } from "./types";
import {
	decodeNativeSelectKey,
	encodeNativeSelectKey,
	getSingleSelectAutocompleteInputWidthStyle,
} from "./selectFieldUtils.ts";
import i18n from "i18next";
import { mergeSx } from "src/utils/styles";
import { EMPTY_SELECTION, LOADING_OPTION_VALUE } from "./constants";
import { isTouchDevice } from "src/utils/isTouchDevice";
import { CustomPopper, CustomPopperProps } from "src/components/common/popper/CustomPopper.tsx";
import { resolveValueOrProvider, ValueOrProvider } from "src/utils/valueOrProvider.ts";

export interface AavoSelectFieldProps<T, Key, DisableClearable extends boolean | undefined>
	extends Omit<
		AutocompleteProps<T, false, DisableClearable, false>,
		| "onChange"
		| "renderInput"
		| "freeSolo"
		| "multiple"
		| "isOptionEqualToValue"
		| "getOptionKey"
		| "getOptionLabel"
		| "value"
		| "defaultValue"
		| "onFocus"
		| "disabled"
		| "slots"
		| "slotProps"
	> {
	onChange?: (
		key: ConditionalNullable<Key, DisableClearable>,
		option: ConditionalNullable<T, DisableClearable>,
	) => void;
	value?: Key | null;
	defaultValue?: Key | null;
	getOptionKey: (o: T) => Key;
	getOptionLabel: (o: T) => string;
	label: string;
	error?: InputError;
	onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>;
	minWidth?: number | string;
	popperProps?: Partial<CustomPopperProps>;
	disabled?: ValueOrProvider<boolean | string>;
	tooltip?: React.ReactNode;
	placeholder?: string;
	helperText?: string;
	TextFieldProps?: Partial<TextFieldProps>;
	/** Content at the end of the field, such as an action button; clicking it does not open the options. */
	endAdornment?: React.ReactNode;
}

export const AavoSelectField = <T, Key, DisableClearable extends boolean | undefined>({
	disabled: disabledProvider,
	tooltip: tooltipProvider,
	...other
}: AavoSelectFieldProps<T, Key, DisableClearable>) => {
	const disabled = resolveValueOrProvider(disabledProvider);
	const isDisabled = typeof disabled === "string" ? disabled.length > 0 : disabled;
	const tooltip = typeof disabled === "string" ? disabled : tooltipProvider;

	if (isTouchDevice()) return <NativeSelectField disabled={isDisabled} {...other} />;
	else return <AutoCompleteSelectField disabled={isDisabled} tooltip={tooltip} {...other} />;
};

interface AutocompleteSelectFieldProps<T, Key, DisableClearable extends boolean | undefined>
	extends Omit<AavoSelectFieldProps<T, Key, DisableClearable>, "tooltip" | "disabled"> {
	tooltip?: React.ReactNode;
	disabled?: boolean;
}

interface NativeSelectFieldProps<T, Key, DisableClearable extends boolean | undefined>
	extends Omit<AavoSelectFieldProps<T, Key, DisableClearable>, "tooltip" | "disabled"> {
	disabled?: boolean;
}

const AutoCompleteSelectField = <T, Key, DisableClearable extends boolean | undefined>({
	label,
	disableClearable,
	value,
	defaultValue,
	onChange,
	options,
	getOptionKey,
	getOptionLabel,
	loading,
	error = undefined,
	onFocus,
	sx,
	minWidth,
	popperProps,
	tooltip,
	autoFocus,
	helperText,
	TextFieldProps,
	endAdornment,
	openOnFocus = true,
	onOpen,
	onClose,
	...other
}: AutocompleteSelectFieldProps<T, Key, DisableClearable>) => {
	const [open, setOpen] = useState(false);
	// Track the first visible option in the filtered list for Enter key handling
	const firstVisibleOptionRef = useRef<T | null>(null);

	const getOptionForKey = (key: Key | null | undefined) => {
		if (key === undefined) return undefined;

		const option =
			options.find((o) => {
				return getOptionKey(o) === key;
			}) ?? null;

		return option as ConditionalNullable<T, DisableClearable>;
	};

	// Use MUI's default filter function
	const defaultFilter = createFilterOptions<T>();
	const handleFilterOptions = (options: T[], state: any) => {
		const filtered = defaultFilter(options, state);
		// Track the first filtered option for Enter key handling
		firstVisibleOptionRef.current = filtered[0] ?? null;
		return filtered;
	};

	return (
		<Tooltip title={tooltip} arrow placement={"bottom-start"}>
			<Autocomplete
				sx={mergeSx(
					{
						minWidth: minWidth,
					},
					sx,
				)}
				onFocus={onFocus}
				autoFocus={autoFocus}
				options={loading ? [] : options}
				getOptionLabel={getOptionLabel}
				filterOptions={handleFilterOptions}
				renderOption={(props, option, state) => {
					const label = getOptionLabel(option);
					// Add enter symbol to first option to indicate that user can press enter to select it
					const displayLabel = state.index === 0 && open ? `${label} \u21B5` : label;
					return <li {...props}>{displayLabel}</li>;
				}}
				disableClearable={disableClearable}
				isOptionEqualToValue={(o1, o2) => {
					if (o1 == null || o2 == null) return false;
					return getOptionKey(o1) === getOptionKey(o2);
				}}
				onChange={(_, newValue) => {
					selectOption(newValue);
				}}
				value={getOptionForKey(value)}
				defaultValue={getOptionForKey(defaultValue)}
				loading={loading}
				openOnFocus={openOnFocus}
				open={open}
				onOpen={(e) => {
					setOpen(true);
					onOpen?.(e);
				}}
				onClose={(e, reason) => {
					setOpen(false);
					onClose?.(e, reason);
				}}
				onKeyDown={async (e) => {
					if (open && e.key === "Enter" && firstVisibleOptionRef.current != null) {
						selectOption(firstVisibleOptionRef.current);
						setOpen(false);
						onClose?.(e, "selectOption");
					}
				}}
				slots={{
					popper: CustomPopper,
				}}
				slotProps={{
					popper: popperProps,
				}}
				renderInput={(params) => {
					return (
						<TextField
							{...params}
							{...TextFieldProps}
							label={label}
							placeholder={TextFieldProps?.placeholder ?? i18n.t("search_options")}
							error={error !== undefined}
							helperText={error ?? helperText ?? undefined}
							slotProps={{
								...params.slotProps,
								...TextFieldProps?.slotProps,
								htmlInput: {
									...params.slotProps.htmlInput,
									...TextFieldProps?.slotProps?.htmlInput,
									style: {
										...getSingleSelectAutocompleteInputWidthStyle(label, params),
										...params.slotProps.htmlInput.style,
									},
								},
								input: {
									...params.slotProps.input,
									...TextFieldProps?.slotProps?.input,
									endAdornment: (
										<>
											{endAdornment != null && (
												<FieldEndAdornment>{endAdornment}</FieldEndAdornment>
											)}
											{loading ?
												<CircularProgress color="inherit" size={20} />
											:	null}
											{params.slotProps.input.endAdornment}
										</>
									),
								},
							}}
						/>
					);
				}}
				{...other}
			/>
		</Tooltip>
	);

	function selectOption(optionToSelect: T | null) {
		if (optionToSelect === null) {
			onChange?.(
				null as ConditionalNullable<Key, DisableClearable>,
				null as ConditionalNullable<T, DisableClearable>,
			);
		} else {
			onChange?.(
				getOptionKey(optionToSelect) as ConditionalNullable<Key, DisableClearable>,
				optionToSelect as ConditionalNullable<T, DisableClearable>,
			);
		}
	}
};

const NativeSelectField = <T, Key, DisableClearable extends boolean | undefined>({
	label,
	disableClearable,
	value,
	defaultValue,
	onChange,
	options: optionsProp,
	getOptionKey,
	getOptionLabel,
	loading,
	error = undefined,
	onFocus,
	sx,
	placeholder,
	disabled,
	helperText,
	endAdornment,
}: NativeSelectFieldProps<T, Key, DisableClearable>) => {
	// Just to make sure that options is not undefined due to e.g. frontend version mismatch
	const options = optionsProp ?? [];

	return (
		<FormControl size={"small"} error={error !== undefined} sx={sx} className={"AavoNativeSelectRoot"}>
			<InputLabel shrink htmlFor="native-select-for-mobile">
				{label}
			</InputLabel>
			<Select
				native
				label={label}
				variant={"outlined"}
				disabled={disabled}
				value={encodeNativeSelectValue(value, loading)}
				defaultValue={defaultValue == undefined ? undefined : encodeNativeSelectKey(defaultValue)}
				onFocus={onFocus}
				endAdornment={
					endAdornment != null && (
						// Leaves room for the native arrow icon at the end.
						<FieldEndAdornment sx={{ marginRight: 3 }}>{endAdornment}</FieldEndAdornment>
					)
				}
				inputProps={{
					name: label,
					id: "native-select-for-mobile",
				}}
				onChange={(e) => {
					const newKey = decodeNativeSelectKey(e.target.value);
					const newOption =
						options.find((o) => {
							return getOptionKey(o) === newKey;
						}) ?? null;
					onChange?.(newKey, newOption as ConditionalNullable<T, DisableClearable>);
				}}
			>
				<option hidden={!loading} disabled={true} value={encodeNativeSelectKey(LOADING_OPTION_VALUE)}>
					{i18n.t("loading...")}
				</option>
				{options.map((o, index) => {
					return (
						<option
							key={`select-field-option-${index}`}
							value={encodeNativeSelectKey(getOptionKey(o))}
							hidden={loading}
						>
							{getOptionLabel(o)}
						</option>
					);
				})}
				<option
					hidden={disableClearable || loading}
					value={encodeNativeSelectKey(EMPTY_SELECTION)}
					disabled={disableClearable}
				>
					{placeholder ?? i18n.t("unselected")}
				</option>
			</Select>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
};

/** Keeps clicks of the adornment from focusing the field, which would open the options. */
function FieldEndAdornment({ children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> }) {
	return (
		<Box
			sx={mergeSx({ display: "flex", alignItems: "center" }, sx)}
			onMouseDown={(e) => e.stopPropagation()}
			onClick={(e) => e.stopPropagation()}
		>
			{children}
		</Box>
	);
}

const encodeNativeSelectValue = <Key,>(value: Key | null | undefined, loading: boolean | undefined) => {
	if (value === undefined) return undefined;
	if (loading === true) return encodeNativeSelectKey(LOADING_OPTION_VALUE);
	if (value === null || value === "") return encodeNativeSelectKey(EMPTY_SELECTION);
	return encodeNativeSelectKey(value);
};
